import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
// MUI
import { Grid, Stack, Typography } from "@mui/material";

// API's
import { getUsersByPositionHistoryPayPlanAssignment } from "@/PayPlans/api/users/queries";

// Hooks
import { useManagedPositions } from "@/Common/hooks/Positions";
import { useLocation } from "@Common/hooks/Location/useLocation";

// Components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import SelectedItemContent from "./SelectedItemContent";
import AssigneesHistory from "./Assignees";
import TimeLineHistoryHeader from "./Timeline";

// Helpers
import { and } from "@Common/helpers/sonar";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { getHistoricalPayPlan, getTimelineNodes } from "@/PayPlans/helpers/Historical";

// Constants
import { DATE_FORMAT } from "@Constants/date";

// Assets
import emptyTable from "@/assets/images/commons/empty_table.png";

// Types
import { IEmployeeDetails, ITimelineNode } from "@PayPlans/types/historical";
import { IPayPlan } from "@PayPlans/types/sections/commons";

type AssignmentsHistoryProps = {
	selectedItem: IPayPlan;
};
export default function AssignmentsHistory(props: AssignmentsHistoryProps) {
	const { selectedItem } = props;
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [nodeTimeLineList, setNodeTimeLineList] = useState<ITimelineNode[]>([]);
	const [employeeList, setEmployeeList] = useState<IEmployeeDetails[]>([]);

	const { timezone, locationId } = useLocation() ?? {};

	const { matchPosition, isLoading: isLoadingPositions } = useManagedPositions({
		queryOpts: {
			enabled: !!selectedItem?.position,
		},
	});

	const position = matchPosition(selectedItem?.position);

	const selectedTitle = selectedItem?.title;
	const selectedPosition = selectedItem?.position;
	const payPlanId = selectedItem?.planId;

	const { data: usersData, isLoading: isLoadingUsers } = useQuery({
		queryKey: ["getUsersByPositionHistoryPayPlanAssignment", locationId, selectedPosition, selectedTitle],
		queryFn: async () => getUsersByPositionHistoryPayPlanAssignment(locationId, position as string[], payPlanId),
		enabled: and(Boolean(locationId), Boolean(position?.length), Boolean(selectedItem)),
		staleTime: Infinity,
	});

	const handleSelectIndex = (index: number) => {
		setSelectedIndex(index);
		setEmployeeList(nodeTimeLineList[index].assigneeDetails);
	};

	useEffect(() => {
		if (!selectedItem.position || !locationId) {
			return;
		}
	}, [locationId, selectedItem?.position]);

	const isGlobalLoading = isLoadingUsers;

	useEffect(() => {
		if (!isLoadingUsers && usersData?.length >= 0) {
			const historialList = getHistoricalPayPlan(usersData, payPlanId);
			const newNodeTimeLineList = getTimelineNodes(historialList, payPlanId);
			const defaultSelect = newNodeTimeLineList.findIndex((item) => item.active);
			const newEmployeeList = newNodeTimeLineList[defaultSelect >= 0 ? defaultSelect : 0]?.assigneeDetails;
			setSelectedIndex(defaultSelect);
			setNodeTimeLineList(newNodeTimeLineList);
			setEmployeeList(newEmployeeList);
		}
	}, [isLoadingUsers, selectedItem]);

	return (
		<SelectedItemContent
			selected={selectedItem}
			filteredData={nodeTimeLineList}
			loading={isGlobalLoading || isLoadingPositions || isLoadingUsers}
		>
			<>
				<RoundedBox sx={{ maxHeight: "min-content" }}>
					<Stack spacing={4}>
						<Typography className="group-pay-plan-assign-container-title">{props.selectedItem.title}</Typography>

						<Stack direction={"row"} spacing={7}>
							<Stack spacing={1}>
								<Stack direction={"row"} spacing={1}>
									<Typography className="pay-plan-assign-history-title">Creation Date:</Typography>
									<Typography className="pay-plan-assign-history-text">
										{getFormattedDate(props.selectedItem.createdAt, timezone, DATE_FORMAT.fullLongTime12hLower)}
									</Typography>
								</Stack>
								<Stack direction={"row"} spacing={1}>
									<Typography className="pay-plan-assign-history-title">Created By:</Typography>
									<Typography className="pay-plan-assign-history-text">
										{props.selectedItem?.createdBy?.fullName}
									</Typography>
								</Stack>
							</Stack>

							<Stack spacing={1}>
								<Stack direction={"row"} spacing={1}>
									<Typography className="pay-plan-assign-history-title">Last Modified:</Typography>
									<Typography className="pay-plan-assign-history-text">
										{getFormattedDate(props.selectedItem.updatedAt, timezone, DATE_FORMAT.fullLongTime12hLower)}
									</Typography>
								</Stack>
								<Stack direction={"row"} spacing={1}>
									<Typography className="pay-plan-assign-history-title">Last Modified By:</Typography>
									<Typography className="pay-plan-assign-history-text">
										{props.selectedItem?.updatedBy?.fullName}
									</Typography>
								</Stack>
							</Stack>
						</Stack>
						<Stack>
							<Typography className="pay-plan-assign-history-text">Notes from assigner:</Typography>
							<Typography mt={1.5} className="pay-plan-assign-history-text"></Typography>
						</Stack>
					</Stack>
				</RoundedBox>
				{!isLoadingUsers && !nodeTimeLineList.length ? (
					<RoundedBox sx={{ marginTop: "20px" }}>
						<EmptyBody
							image={emptyTable}
							title={"No history for this Pay Plan."}
							bodyHeight="300px"
							sx={{
								width: "auto",
								boxShadow: "none !important",
							}}
						/>
					</RoundedBox>
				) : (
					<RoundedBox sx={{ marginTop: "20px", maxHeight: "min-content" }}>
						<Grid
							container
							sx={{
								columnGap: 0,
								alignItems: "top",
								marginTop: "10px",
								marginLeft: "10px",
								marginRight: "32px",
							}}
						>
							<Grid item xs={4}>
								<TimeLineHistoryHeader
									nodeTimeLineList={nodeTimeLineList}
									selectedIndex={selectedIndex}
									setSelectedIndexParent={handleSelectIndex}
								/>
							</Grid>
							<Grid item xs={8}>
								<AssigneesHistory assigneesDetailsList={employeeList} />
							</Grid>
						</Grid>
					</RoundedBox>
				)}
			</>
		</SelectedItemContent>
	);
}
